import getNudgedRowData from '../services/getNudgedRowData';
import getNudgesOperation from '../services/getNudgesOperation';
import { Nudge } from '../types/GetNudgesResponse';
import getClientNudgesIfTesting from '../utils/getClientNudgesIfTesting';
import getNudgedReasonFromResponse from '../utils/getNudgedReasonFromResponse';
import isNudgeSupportedInFolder, {
    isNudgeEnabled,
} from 'owa-mail-nudge-store/lib/utils/isNudgeSupported';
import updatePreviouslyNudgedRowToOriginalPosition from '../utils/updatePreviouslyNudgedRowToOriginalPosition';
import { logUsage } from 'owa-analytics';
import { createLazyOrchestrator } from 'owa-bundling';
import { getGuid } from 'owa-guid';
import { onSelectFolderComplete } from 'owa-mail-shared-actions/lib/onSelectFolderComplete';
import { getFirstUnpinnedRowIndex } from 'owa-mail-list-response-processor';
import TableOperations from 'owa-mail-list-table-operations';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import { getStore, NudgedRow, onGetNudgesCompleted, NudgedReason } from 'owa-mail-nudge-store';
import { lazyLoadConversation, lazyLoadItem } from 'owa-mail-store-actions';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { trace } from 'owa-trace';
import {
    getSelectedTableView,
    getRowKeysFromRowIds,
    TableView,
    TableQuery,
    MailRowDataPropertyGetter,
    isConversationView,
} from 'owa-mail-list-store';
import { differenceInCalendarDays, userDate } from 'owa-datetime';
import { getTestNudgeRowDataForRowNotLoadedInTableView } from './helpers/getTestNudgeRows';
import { getQueryStringParameter } from 'owa-querystring';
import { logNudgeRemoved } from '../utils/logNudgeRemoved';
import { onNudgeIsEnableSaved } from 'owa-mail-shared-actions/lib/onNudgeIsEnableSaved';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';

// this variable is used to ensure that
// only one GetNudge call is issued at a time
let isLoadingNudge: boolean = false;

export const getNudgesOnNudgeOptionChange = createLazyOrchestrator(
    onNudgeIsEnableSaved,
    'getNudgesOnNudgeOptionChangeClone',
    actionMessage => {
        const tableView = getSelectedTableView();
        getNudgesInternal(tableView.tableQuery.folderId);
    }
);

export const getNudgesOnSelectFolderComplete = createLazyOrchestrator(
    onSelectFolderComplete,
    'clone_onSelectFolderComplete',
    actionMessage => {
        // Do not issue getNudges call if
        // the switch folder has happened to the same folder node or
        // user is exiting search that ends up selecting previous folder node
        if (
            actionMessage.selectedFolderId != actionMessage.previousFolderId &&
            !actionMessage.isExitingSearch
        ) {
            getNudgesInternal(actionMessage.selectedFolderId);
        }
    }
);

async function getNudgesInternal(selectedFolderId: string) {
    if (isLoadingNudge || !isNudgeEnabled() || !isNudgeSupportedInFolder(selectedFolderId)) {
        return;
    }

    const tableView = getSelectedTableView();
    const folderId = tableView.tableQuery.folderId;
    if (folderId != selectedFolderId) {
        // selected folder and table view folder may not be the same briefly when
        // user refreshes owa in a non-inbox folder
        return;
    }

    isLoadingNudge = true;
    const correlationId = getGuid();
    const startNudgeTime = Date.now();
    const getNudgesResponse = await getNudgesOperation(correlationId);
    const getNudgeLatency = getNudgesResponse ? Date.now() - startNudgeTime : -1;
    const newNudges: NudgedRow[] = [];
    const nudgesFromServer = getNudgesResponse?.Nudges || [];
    let totalNudgesForCurrentTable = 0;
    const itemIdsToLog = [];
    const sentItemsFolderId = folderIdToName(selectedFolderId) == 'sentitems';
    for (let i = 0; i < nudgesFromServer.length; i++) {
        const nudge = nudgesFromServer[i];
        const nudgeFolderId = nudge.FolderId.Id;
        itemIdsToLog.push(nudge.ItemId.Id);

        // For Sent Items folder only show nudges that belong to sentItems
        // For Inbox try to show all nudges. e.g. a follow up nudge item could belong to a conversation that is in Inbox
        if (sentItemsFolderId && nudgeFolderId != selectedFolderId) {
            continue;
        }

        const nudgedRow = await processNudgeResponse(nudge, tableView, correlationId);
        if (nudgedRow) {
            newNudges.push(nudgedRow);
        }

        totalNudgesForCurrentTable++;
    }

    logUsage('Nudge_GetNudge', {
        owa_1:
            nudgesFromServer.length /* totalNudgeCount - we get nudges for inbox and sentitems */,
        owa_2: totalNudgesForCurrentTable /* we get nudges for inbox and sentitems */,
        owa_3: newNudges.length /* count of nudge rows for which we found data */,
        cId: correlationId,
        itemId: itemIdsToLog.join(','),
        latency: getNudgeLatency /* GetNudges call latency, -1 if call failed */,
    });

    const testNudgeURIString = getQueryStringParameter('testOldNudge');
    const testNudgeString = decodeURIComponent(testNudgeURIString || '').trim();
    if (testNudgeString.length != 0) {
        newNudges.push({
            rowKey: 'testNudgeRowKey',
            itemId: 'getTestNudgeRowDataForRowNotLoadedInTableViewItemId=',
            conversationId: 'getTestNudgeRowDataForRowNotLoadedInTableViewConversationId=',
            tableViewId: tableView.id,
            reason: NudgedReason.ReceivedDaysAgo,
            daysAgo: 7,
        });
        const newOldNudgeRow = getTestNudgeRowDataForRowNotLoadedInTableView();

        if (tableView.rowKeys.indexOf('testNudgeRowKey') < 0) {
            const indexToInsertAt = getFirstUnpinnedRowIndex(tableView, 0 /*startIndex*/);
            TableOperations.addRow(indexToInsertAt, newOldNudgeRow, tableView);
        }
    }

    newNudges.push(...getClientNudgesIfTesting(tableView));

    /**
     * Update positions for existing nudges in the current table if we have not received them as nudge anymore.
     * GetNudges call may return a different nudge from a previous call,
     * and client needs to return the item that we had previous nudged,
     * that are not in the latest response, to its original position
     */
    getStore().nudgedRows.forEach(oldNudgedRow => {
        const oldNudgeRowKey = oldNudgedRow.rowKey;

        if (
            oldNudgedRow.tableViewId == tableView.id &&
            !newNudges.some(
                newNudgedRow =>
                    newNudgedRow.rowKey === oldNudgeRowKey &&
                    newNudgedRow.tableViewId === oldNudgedRow.tableViewId
            )
        ) {
            updatePreviouslyNudgedRowToOriginalPosition(oldNudgeRowKey, tableView);
            logNudgeRemoved(oldNudgeRowKey, 'NotReceivedAsNudgeAnyMore', oldNudgedRow.reason);
        }

        /**
         * We should not be overriding the nudges that are in the store for the table that is not
         * currently selected. The reason being when switching back to this table we still want to
         * know if a row is nudge or not before we end up making a new GetNudges call for that table.
         */
        if (oldNudgedRow.tableViewId != tableView.id) {
            newNudges.push(oldNudgedRow);
        }
    });

    // Add nudges to store
    // Add nudges to list view
    onGetNudgesCompleted(newNudges, tableView);

    trace.info('Nudge: onSelectFolderCompleteOrchestrator new nudges length: ' + newNudges.length);

    isLoadingNudge = false;
}

async function processNudgeResponse(
    nudge: Nudge,
    tableView: TableView,
    correlationId: string
): Promise<NudgedRow> {
    try {
        const itemId = nudge.ItemId.Id;
        const conversationId = nudge.ConversationId.Id;

        const isConversationListViewType = isConversationView(tableView);
        const rowId = isConversationListViewType ? conversationId : itemId;

        let nudgedRowKey;
        const rowKeys = getRowKeysFromRowIds([rowId], tableView);

        if (rowKeys.length === 0) {
            // row was not found in the client table,
            // try to fetch its data and add to table
            const nudgedRowToAdd = await getNudgedRowData(rowId, tableView);
            if (!nudgedRowToAdd) {
                trace.warn(
                    'Nudge: Unable to load nudged row for list view type:' +
                        tableView.tableQuery.listViewType +
                        ' itemId: ' +
                        itemId +
                        ' conversationId: ' +
                        conversationId +
                        ' folderId: ' +
                        tableView.tableQuery.folderId
                );
                logNudgeDiscarded(
                    'RowNotFound',
                    itemId,
                    conversationId,
                    correlationId,
                    isConversationListViewType
                );
                return null;
            }

            nudgedRowKey = nudgedRowToAdd.InstanceKey;

            /**
             * Do not add the row again if the row has been added by the time
             * our data returns.
             */
            if (tableView.rowKeys.indexOf(nudgedRowKey) < 0) {
                const indexToInsertAt = getFirstUnpinnedRowIndex(tableView, 0 /*startIndex*/);
                TableOperations.addRow(indexToInsertAt, nudgedRowToAdd, tableView);
            }
        } else {
            nudgedRowKey = rowKeys[0];

            // Row was found in the list view table
            // row is pinned log this data
            if (MailRowDataPropertyGetter.getIsPinned(nudgedRowKey, tableView)) {
                logNudgeDiscarded(
                    'RowPinned',
                    itemId,
                    conversationId,
                    correlationId,
                    isConversationListViewType
                );
            }

            // Prefetch row data if necessary
            prefetchNudgeDataIfNeeded(tableView.tableQuery, rowId);
        }

        // Use received or sent time on nudge if present when calculating age
        const lastDeliveryTime =
            nudge.MessageReceivedOrSentTimeUTC ||
            MailRowDataPropertyGetter.getLastDeliveryTimeStamp(nudgedRowKey, tableView);
        const ageInDays = differenceInCalendarDays(userDate(), userDate(lastDeliveryTime));

        return {
            rowKey: nudgedRowKey,
            itemId: itemId,
            conversationId: conversationId,
            tableViewId: tableView.id,
            reason: getNudgedReasonFromResponse(nudge.NudgeReason.Reason),
            daysAgo: ageInDays,
        };
    } catch (error) {
        // in case any of the required fields are null in the server response
        // client will ignore the exception and move on
        trace.warn('Nudge: processNudgeResponse throws an exception' + error.message);
        return null;
    }
}

function logNudgeDiscarded(
    reason: string,
    itemId: string,
    conversationId: string,
    correlationId: string,
    isConversationView: boolean
) {
    logUsage('Nudge_Discarded', {
        owa_1: reason,
        owa_2: isConversationView,
        itemId: itemId,
        cId: conversationId,
        crId: correlationId,
    });
}

function prefetchNudgeDataIfNeeded(tableQuery: TableQuery, rowId: string) {
    if (tableQuery.listViewType === ReactListViewType.Message) {
        const cachedItem = getMailStore().items.get(rowId);
        if (!cachedItem || !cachedItem.NormalizedBody) {
            lazyLoadItem.importAndExecute(
                {
                    Id: rowId,
                    mailboxInfo: getMailboxInfoFromTableQuery(tableQuery),
                },
                'LoadNudgedItem'
            );
        }
    } else if (!getMailStore().conversations.get(rowId)) {
        lazyLoadConversation.importAndExecute(
            {
                Id: rowId,
                mailboxInfo: getMailboxInfoFromTableQuery(tableQuery),
            },
            'LoadNudgedConversation'
        );
    }
}
